import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactEventHandler,
  ReactNode,
  Ref,
} from 'react';
import { merge } from 'lodash-es';
import React, { createContext, useContext } from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { ShortcutKey } from '../../formatting/Shortcut/Shortcut';
import { Icon } from '../../assets/Icon/Icon';
import { fade, palette } from '../../common/colors';
import { devices } from '../../common/devices';
import { FocusRing } from '../../common/focus_rings';
import { outlines } from '../../common/outlines';
import { selectors } from '../../common/selectors';
import { getVariantStyles, variantIconColor } from '../../common/variant';
import { stateZIndexes } from '../../common/z_indexes';
import { Tooltip, useInsideTooltipTrigger } from '../../components/Tooltip/Tooltip';
import Shortcut, { shortcutsVisible } from '../../formatting/Shortcut/Shortcut';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { type DirectionProps, directionCSS } from '../../utilities/shared/Direction';
import { transitions } from '../shared/styles';

type UtilityControlPlacement = 'ends' | 'inline' | 'sides';
type UtilityControlVariant = VariantProp | 'application';

type UtilityControlGroupContextValue = {
  placement?: UtilityControlPlacement;
  variant?: UtilityControlVariant;
  visible?: boolean;
};

const UtilityControlGroupContext = createContext<UtilityControlGroupContextValue | undefined>(
  undefined,
);
const UtilityControlGroupProvider = UtilityControlGroupContext.Provider;

const useUtilityControlGroupPlacement = (
  controlledValue: UtilityControlPlacement | undefined,
  defaultValue: UtilityControlPlacement = 'inline',
): UtilityControlPlacement => {
  const contextValue = useContext(UtilityControlGroupContext);
  return controlledValue ?? contextValue?.placement ?? defaultValue;
};

const useUtilityControlGroupVariant = (
  controlledValue: UtilityControlVariant | undefined,
  defaultValue: UtilityControlVariant = 'neutral',
): UtilityControlVariant => {
  const contextValue = useContext(UtilityControlGroupContext);
  return controlledValue ?? contextValue?.variant ?? defaultValue;
};

// No default: `undefined` means nobody is driving visibility, so the control is always shown.
const useUtilityControlGroupVisible = (
  controlledValue: boolean | undefined,
): boolean | undefined => {
  const contextValue = useContext(UtilityControlGroupContext);
  return controlledValue ?? contextValue?.visible;
};

const utilityControlCalloutPosition = '25%';

const UtilityControlCallout = styled('div', {
  position: 'absolute',
  top: `-${utilityControlCalloutPosition}`,
  right: `-${utilityControlCalloutPosition}`,
  widthAll: '$8',
  heightAll: '$8',
  background: variantIconColor,
  borderRadius: '99rem',
  transform: `translate(-${utilityControlCalloutPosition}, ${utilityControlCalloutPosition})`,

  variants: {
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
  },
});
UtilityControlCallout.displayName = 'UtilityControlCallout';

const UtilityControlIcon = styled(Icon, {
  color: '$$iconColor',

  [devices.touch]: {
    size: '$12',
  },

  [devices.mouse]: {
    size: '$10',
  },
});
UtilityControlIcon.displayName = 'UtilityControlIcon';

const UtilityControlContainer = styled('button', FocusRing, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: transitions.control,
  outline: outlines.control,
  outlineColor: colors.transparent,

  [devices.touch]: {
    borderRadius: '$8',
  },

  [devices.mouse]: {
    borderRadius: '$6',
  },

  variants: {
    disabled: {
      true: {
        pointerEvents: 'none',
      },
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    placement: {
      ends: {
        [devices.touch]: {
          widthAll: '$36',
          heightAll: '$24',
        },

        [devices.mouse]: {
          widthAll: '$28',
          heightAll: '$16',
        },
      },
      inline: {
        [devices.touch]: {
          size: '$28',
        },

        [devices.mouse]: {
          size: '$20',
        },
      },
      sides: {
        [devices.touch]: {
          widthAll: '$24',
          heightAll: '$36',
        },

        [devices.mouse]: {
          widthAll: '$16',
          heightAll: '$28',
        },
      },
    },
    variant: {
      alternative: {},
      attention: {},
      brand: {},
      negative: {},
      neutral: {},
      positive: {},
      application: {},
    },
    visible: {
      true: { opacity: 1 },
      false: { opacity: 0, pointerEvents: 'none' },
    },
  },

  compoundVariants: [
    {
      disabled: true,
      css: {
        opacity: 0.5,
        cursor: 'not-allowed',

        [selectors.hover]: {
          opacity: 0.5,
        },

        [selectors.focus]: {
          opacity: 0.5,
        },
      },
    },
    {
      disabled: false,
      placement: 'ends',
      variant: 'alternative',
      css: {
        outlineColor: colors.strokeAlternativeLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeAlternativeDark,
        },
      },
    },
    {
      disabled: false,
      placement: 'sides',
      variant: 'alternative',
      css: {
        outlineColor: colors.strokeAlternativeLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeAlternativeDark,
        },
      },
    },
    {
      disabled: false,
      variant: 'alternative',
      css: {
        backgroundColor: fade(palette.bgAlternativeLight, 0.7),
        $$iconColor: colors.iconAlternativeLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgAlternativeDark, 0.7),
          $$iconColor: colors.iconAlternativeDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgAlternativeLight, 0.9),
          outlineColor: colors.strokeAlternativeLight,
          $$iconColor: colors.bodyAlternativeLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgAlternativeDark, 0.9),
            outlineColor: colors.strokeAlternativeDark,
            $$iconColor: colors.bodyAlternativeDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgAlternativeLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgAlternativeDark,
          },
        },
      },
    },
    {
      disabled: false,
      placement: 'ends',
      variant: 'attention',
      css: {
        outlineColor: colors.strokeAttentionLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeAttentionDark,
        },
      },
    },
    {
      disabled: false,
      placement: 'sides',
      variant: 'attention',
      css: {
        outlineColor: colors.strokeAttentionLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeAttentionDark,
        },
      },
    },
    {
      disabled: false,
      variant: 'attention',
      css: {
        backgroundColor: fade(palette.bgAttentionLight, 0.7),
        $$iconColor: colors.iconAttentionLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgAttentionDark, 0.7),
          $$iconColor: colors.iconAttentionDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgAttentionLight, 0.9),
          outlineColor: colors.strokeAttentionLight,
          $$iconColor: colors.bodyAttentionLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgAttentionDark, 0.9),
            outlineColor: colors.strokeAttentionDark,
            $$iconColor: colors.bodyAttentionDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgAttentionLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgAttentionDark,
          },
        },
      },
    },
    {
      disabled: false,
      placement: 'ends',
      variant: 'brand',
      css: {
        outlineColor: colors.strokeBrandLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeBrandDark,
        },
      },
    },
    {
      disabled: false,
      placement: 'sides',
      variant: 'brand',
      css: {
        outlineColor: colors.strokeBrandLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeBrandDark,
        },
      },
    },
    {
      disabled: false,
      variant: 'brand',
      css: {
        backgroundColor: fade(palette.bgBrandLight, 0.7),
        $$iconColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgBrandDark, 0.7),
          $$iconColor: colors.iconBrandDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgBrandLight, 0.9),
          outlineColor: colors.strokeBrandLight,
          $$iconColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgBrandDark, 0.9),
            outlineColor: colors.strokeBrandDark,
            $$iconColor: colors.bodyBrandDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
          },
        },
      },
    },
    {
      disabled: false,
      placement: 'ends',
      variant: 'negative',
      css: {
        outlineColor: colors.strokeNegativeLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeNegativeDark,
        },
      },
    },
    {
      disabled: false,
      placement: 'sides',
      variant: 'negative',
      css: {
        outlineColor: colors.strokeNegativeLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeNegativeDark,
        },
      },
    },
    {
      disabled: false,
      variant: 'negative',
      css: {
        backgroundColor: fade(palette.bgNegativeLight, 0.7),
        $$iconColor: colors.iconNegativeLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgNegativeDark, 0.7),
          $$iconColor: colors.iconNegativeDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgNegativeLight, 0.9),
          outlineColor: colors.strokeNegativeLight,
          $$iconColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgNegativeDark, 0.9),
            outlineColor: colors.strokeNegativeDark,
            $$iconColor: colors.bodyNegativeDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgNegativeLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNegativeDark,
          },
        },
      },
    },
    {
      disabled: false,
      placement: 'ends',
      variant: 'neutral',
      css: {
        outlineColor: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeNeutralDark,
        },
      },
    },
    {
      disabled: false,
      placement: 'sides',
      variant: 'neutral',
      css: {
        outlineColor: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokeNeutralDark,
        },
      },
    },
    {
      disabled: false,
      variant: 'neutral',
      css: {
        backgroundColor: fade(palette.bgNeutralLight, 0.7),
        $$iconColor: colors.iconNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgNeutralDark, 0.7),
          $$iconColor: colors.iconNeutralDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgNeutralLight, 0.9),
          outlineColor: colors.strokeNeutralLight,
          $$iconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgNeutralDark, 0.9),
            outlineColor: colors.strokeNeutralDark,
            $$iconColor: colors.bodyNeutralDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
          },
        },
      },
    },
    {
      disabled: false,
      placement: 'ends',
      variant: 'positive',
      css: {
        outlineColor: colors.strokePositiveLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokePositiveDark,
        },
      },
    },
    {
      disabled: false,
      placement: 'sides',
      variant: 'positive',
      css: {
        outlineColor: colors.strokePositiveLight,

        [darkThemeSelector]: {
          outlineColor: colors.strokePositiveDark,
        },
      },
    },
    {
      disabled: false,
      variant: 'positive',
      css: {
        backgroundColor: fade(palette.bgPositiveLight, 0.7),
        $$iconColor: colors.iconPositiveLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgPositiveDark, 0.7),
          $$iconColor: colors.iconPositiveDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgPositiveLight, 0.9),
          outlineColor: colors.strokePositiveLight,
          $$iconColor: colors.bodyPositiveLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgPositiveDark, 0.9),
            outlineColor: colors.strokePositiveDark,
            $$iconColor: colors.bodyPositiveDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgPositiveLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgPositiveDark,
          },
        },
      },
    },
    {
      disabled: false,
      placement: 'ends',
      variant: 'application',
      css: {
        outlineColor: fade(palette.strokeNeutralLight, 0.7),

        [darkThemeSelector]: {
          outlineColor: fade(palette.strokeNeutralDark, 0.7),
        },
      },
    },
    {
      disabled: false,
      placement: 'sides',
      variant: 'application',
      css: {
        outlineColor: fade(palette.strokeNeutralLight, 0.7),

        [darkThemeSelector]: {
          outlineColor: fade(palette.strokeNeutralDark, 0.7),
        },
      },
    },
    {
      disabled: false,
      variant: 'application',
      css: {
        backgroundColor: fade(palette.bgApplicationLight, 0.7),
        $$iconColor: colors.iconNeutralLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.bgApplicationDark, 0.7),
          $$iconColor: colors.iconNeutralDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.bgNeutralLight, 0.7),
          outlineColor: fade(palette.strokeNeutralLight, 0.7),
          $$iconColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.bgNeutralDark, 0.7),
            outlineColor: fade(palette.strokeNeutralDark, 0.7),
            $$iconColor: colors.bodyNeutralDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.bgNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,
          },
        },
      },
    },
    {
      internal: true,
      placement: 'ends',
      css: {
        outlineColor: colors.internalStrokeLight,

        [darkThemeSelector]: {
          outlineColor: colors.internalStrokeDark,
        },
      },
    },
    {
      internal: true,
      placement: 'sides',
      css: {
        outlineColor: colors.internalStrokeLight,

        [darkThemeSelector]: {
          outlineColor: colors.internalStrokeDark,
        },
      },
    },
    {
      internal: true,
      css: {
        backgroundColor: fade(palette.internalBgLight, 0.7),
        $$iconColor: colors.internalIconLight,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.internalBgDark, 0.7),
          $$iconColor: colors.internalIconDark,
        },

        [selectors.hover]: {
          backgroundColor: fade(palette.internalBgLight, 0.9),
          outlineColor: colors.internalStrokeLight,
          $$iconColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            backgroundColor: fade(palette.internalBgDark, 0.9),
            outlineColor: colors.internalStrokeDark,
            $$iconColor: colors.internalBodyDark,
          },
        },

        [selectors.focus]: {
          backgroundColor: colors.internalBgLight,

          [darkThemeSelector]: {
            backgroundColor: colors.internalBgDark,
          },
        },
      },
    },
  ],
});
UtilityControlContainer.displayName = 'UtilityControlContainer';

export type UtilityControlProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * Whether the control is currently active (being interacted with).
   */
  active?: boolean;
  /**
   * The callout variant of the control.
   */
  callout?: VariantProp;
  /**
   * Whether the control is disabled.
   */
  disabled?: boolean;
  /**
   * The icon to display in the control.
   */
  icon?: IconName;
  /**
   * Whether the control is internal.
   */
  internal?: boolean;
  /**
   * Required! The label of the control.
   */
  label: ReactNode;
  /**
   * The event handler for the control.
   */
  onClick?: ReactEventHandler;
  /**
   * How it will be placed, `ends` is on the top/bottom boundary of an object, `sides` is
   * on the start/end boundary of an object, and `inline` is like a typical control.
   */
  placement?: UtilityControlPlacement;
  /**
   * Set shortcut keys for the control.
   */
  shortcut?: ShortcutKey[];
  /**
   * The variant of the control.
   */
  variant?: UtilityControlVariant;
  /**
   * Controls opacity visibility with a smooth transition. Pair with `useUtilityVisibility`.
   * When omitted the control is always fully visible.
   */
  visible?: boolean;
  /**
   * The ref of the control.
   */
  ref?: Ref<HTMLButtonElement | null>;
};

export function UtilityControl({
  callout,
  disabled = false,
  icon = 'cross',
  internal = false,
  active = false,
  label,
  onClick,
  placement,
  shortcut,
  variant,
  visible,
  ref,
  ...remaining
}: UtilityControlProps) {
  const relativePlacement = useUtilityControlGroupPlacement(placement, 'inline');
  const relativeVariant = useUtilityControlGroupVariant(variant, 'neutral');
  const relativeVisible = useUtilityControlGroupVisible(visible);
  const insideTooltipTrigger = useInsideTooltipTrigger();

  const content = (
    <UtilityControlContainer
      ref={ref}
      type="button"
      onClick={!disabled ? onClick : undefined}
      tabIndex={!disabled ? 0 : -1}
      aria-label={`${label}`}
      disabled={disabled}
      internal={internal}
      placement={relativePlacement}
      variant={relativeVariant}
      visible={visible}
      {...remaining}
    >
      {callout != null && <UtilityControlCallout variant={callout} />}
      <UtilityControlIcon icon={icon} />
    </UtilityControlContainer>
  );

  if (disabled || active || insideTooltipTrigger) {
    return content;
  }

  return (
    <Tooltip
      contents={
        <Text>
          {shortcut && shortcutsVisible ? (
            <>
              {label} <Shortcut keys={shortcut} />
            </>
          ) : (
            label
          )}
        </Text>
      }
      toggle={false}
      // A hidden control must not keep a live tooltip. The tooltip is portaled, so the cursor
      // reaching it counts as leaving whatever container was keeping the control visible — the
      // control fades out and the tooltip is left anchored to nothing.
      disabled={relativeVisible === false}
    >
      {content}
    </Tooltip>
  );
}

export type UtilityControlDecorationProps = HTMLAttributes<HTMLSpanElement> & {
  /**
   * The callout variant of the decoration.
   */
  callout?: VariantProp;
  /**
   * The icon to display in the decoration.
   */
  icon?: IconName;
  /**
   * Whether the decoration is internal.
   */
  internal?: boolean;
  /**
   * How it will be placed, `ends` is on the top/bottom boundary of an object, `sides` is
   * on the start/end boundary of an object, and `inline` is like a typical control.
   */
  placement?: UtilityControlPlacement;
  /**
   * The variant of the decoration.
   */
  variant?: UtilityControlVariant;
};

/**
 * A `UtilityControl` that is only a picture of one: same box, same icon, no button underneath.
 *
 * Reach for it when the thing that reacts to the press is an ancestor — a collapsible header, a
 * removable token — so the chrome can look like a control without being a second button nested
 * inside the real one. It is hidden from the reader, so the element around it has to carry the name.
 */
export function UtilityControlDecoration({
  callout,
  icon = 'cross',
  internal = false,
  placement,
  variant,
  ...remaining
}: UtilityControlDecorationProps) {
  const relativePlacement = useUtilityControlGroupPlacement(placement, 'inline');
  const relativeVariant = useUtilityControlGroupVariant(variant, 'neutral');

  return (
    <UtilityControlContainer
      as="span"
      aria-hidden
      // `disabled` here is the style variant, not the DOM attribute — it is what selects the
      // compound variants that paint the resting background, so a decoration has to say it is not.
      disabled={false}
      internal={internal}
      placement={relativePlacement}
      variant={relativeVariant}
      {...remaining}
    >
      {callout != null && <UtilityControlCallout variant={callout} />}
      <UtilityControlIcon icon={icon} />
    </UtilityControlContainer>
  );
}

const createChildSelector = (pseudoClass = '') =>
  `& ${UtilityControlContainer}${pseudoClass}:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true'])`;

const createFocusedChildSelector = (pseudoClass = '') => `
& ${UtilityControlContainer}${pseudoClass}:focus-visible,
& ${UtilityControlContainer}${pseudoClass}:has(:focus-visible),
& ${UtilityControlContainer}${pseudoClass}[data-focus-visible='true']`;

const resetChildSelector = createChildSelector();
const firstChildSelector = `
& ${UtilityControlContainer}:first-child:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true'])`;
const lastChildSelector = `
& ${UtilityControlContainer}:last-child:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true'])`;
const hoveredChildSelector = createChildSelector(':hover');
const focusedChildSelector = createFocusedChildSelector();

const UtilityControlGroupContainer = styled('div', {
  display: 'flex',
  gap: '$1',
  transition: transitions.control,

  [resetChildSelector]: {
    zIndex: stateZIndexes.base,
  },

  [hoveredChildSelector]: {
    zIndex: stateZIndexes.hover,
  },

  [focusedChildSelector]: {
    zIndex: stateZIndexes.focus,
  },

  variants: {
    direction: {
      column: {},
      'column-reverse': {},
      row: {},
      'row-reverse': {},
    },
    placement: {
      ends: {},
      inline: {},
      sides: {},
    },
    relation: {
      joined: {
        [resetChildSelector]: {
          borderRadius: 0,
        },
      },
      separate: {},
    },
    visible: {
      true: { opacity: 1 },
      false: { opacity: 0, pointerEvents: 'none' },
    },
  },

  compoundVariants: [
    {
      direction: 'row',
      placement: 'ends',
      relation: 'joined',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$4',
          borderBottomLeftRadius: '$4',
        },

        [lastChildSelector]: {
          borderTopRightRadius: '$4',
          borderBottomRightRadius: '$4',
        },
      },
    },
    {
      direction: 'column',
      placement: 'ends',
      relation: 'joined',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$4',
          borderTopRightRadius: '$4',
        },

        [lastChildSelector]: {
          borderBottomLeftRadius: '$4',
          borderBottomRightRadius: '$4',
        },
      },
    },
    {
      direction: 'row',
      placement: 'sides',
      relation: 'joined',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$4',
          borderBottomLeftRadius: '$4',
        },

        [lastChildSelector]: {
          borderTopRightRadius: '$4',
          borderBottomRightRadius: '$4',
        },
      },
    },
    {
      direction: 'column',
      placement: 'sides',
      relation: 'joined',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$4',
          borderTopRightRadius: '$4',
        },

        [lastChildSelector]: {
          borderBottomLeftRadius: '$4',
          borderBottomRightRadius: '$4',
        },
      },
    },
  ],
});
UtilityControlGroupContainer.displayName = 'UtilityControlGroupContainer';

export type UtilityControlGroupProps = {
  children: ReactNode;
  placement?: UtilityControlPlacement;
  relation: 'joined' | 'separate';
  variant?: UtilityControlVariant;
  visible?: boolean;
  ref?: Ref<HTMLDivElement | null>;
} & DirectionProps;

export function UtilityControlGroup({
  children,
  direction,
  placement,
  relation = 'joined',
  variant,
  visible,
  ref,
  ...remaining
}: UtilityControlGroupProps) {
  const css = merge(directionCSS(direction));

  return (
    <UtilityControlGroupContainer
      css={css}
      direction={direction}
      relation={relation}
      placement={placement}
      visible={visible}
      ref={ref}
      {...remaining}
    >
      <UtilityControlGroupProvider value={{ placement, variant, visible }}>
        {children}
      </UtilityControlGroupProvider>
    </UtilityControlGroupContainer>
  );
}
